import { load } from './api/load'
import { log } from './api/log'
import { error } from './api/error'
import { resolveLocation } from './api/resolve-location'
import { track, trackForm, trackLink, trackElement, trackSubmit } from './api/track'
import { setupAutoTrack } from './api/auto-track'
import { page } from './api/page'
import { identify } from './api/identify'
import { ready, getMetas, getContext } from './api/ready'
import { isBot } from './api/is-bot'
import { isPwa } from './api/is-pwa'
import { hasContactInfo, onContactInfoChange } from './api/has-contact-info'
import { trackOptInPush } from './api/track-opt-in-push'
import { ads } from './api/ads'
import { cmsApi } from './api/cms'
import { cookieConsent } from './api/cookie-consent'
import { reset } from './api/reset'
import { state } from './state'
import type { MiddlewareFn } from './middleware/pipeline'
import type { MontillaPluginNamespaces } from './types/plugin'
import { globalSingleton } from './utils/global-singleton'

export * from './types'
export type {
  MontillaPlugin,
  PluginContext,
  MontillaPluginNamespaces,
  MontillaPublicApi,
} from './types/plugin'
export { getOrderSessionStore, resetOrderSessionStore } from './stores/order-session-store'
export type { OrderStatus, CartItem } from './stores/order-session-store'
export type { GtmBridgeConfig, GtmConfig } from './types/gtm'
export type {
  CookieConsentConfig,
  CookieConsentApi,
  ConsentState,
  CookieConsentPart,
} from './types/cookie-consent'
export type {
  MontillaDestination,
  EventPayload,
  MontillaContext,
  PageType,
} from './types/destination'
export type { EventEvent, EventAttributes } from '@grafana/faro-web-sdk'

export { registerMontillaPlugin, resolvePluginByName, getRegisteredPlugins } from './plugins/plugin-registry'
export { APP_KEY } from './constants'
export { setDistributionChannel, getDistributionChannel } from './channel'
export { getCoreInstanceCount } from './utils/global-singleton'
export {
  resolveDisplayCategory,
  resolveDisplayColor,
  EVENT_CATEGORY_COLORS,
} from './utils/event-meta'
export type { EventDisplayCategory } from './utils/event-meta'
export { setupAutoTrack } from './api/auto-track'
export type { AutoTrackOptions, AutoTrackHandle } from './api/auto-track'
export { buildAdLinkId, parseAdLinkId, slugifyAdSegment } from './utils/ad-link'
export type { AdLinkParts, AdLinkInput } from './utils/ad-link'
export type { AdLinkOptions } from './api/ads'
export type {
  AdFormat,
  AdOffer,
  AdOfferTracking,
  AdDecision,
  AdsResponse,
  AdsNoFill,
  AdsResult,
  AdRenderContext,
  AdRenderOptions,
  AdSlotHandle,
  AdSlotInspection,
  AdSlotInspectionOffer,
  AdsDryRunCandidate,
  AdsExplanation,
  AdExplainOptions,
} from './types/ads-render'
export { MONTILLA_VISITOR_HEADER, MONTILLA_PREVIEW_HEADER, ADS_PATH } from './types/ads-render'

/**
 * Register a middleware to process events before they reach destinations.
 * @example
 * ```ts
 * montilla.use((event, next) => {
 *   event.properties.ab_variant = getActiveVariant()
 *   next()
 * })
 * ```
 */
function use(fn: MiddlewareFn): void {
  state.eventBus.use(fn)
}

/**
 * The montilla singleton.
 *
 * NOTE: this object is intentionally NOT `Object.freeze`d — plugins installed
 * via `montilla.load({ plugins: [...] })` are attached as new namespaces at
 * runtime using `Object.defineProperty` (non-writable, non-configurable).
 * The built-in methods above cannot be overwritten because plugin names that
 * collide with them are rejected in `installPlugins`.
 *
 * @see packages/docs/content/plugins.md
 */
const montillaBase = {
  load,
  log,
  error,
  resolveLocation,
  track,
  trackForm,
  trackLink,
  trackElement,
  trackSubmit,
  setupAutoTrack,
  trackOptInPush,
  page,
  identify,
  ready,
  isBot,
  isPwa,
  hasContactInfo,
  onContactInfoChange,
  getMetas,
  getContext,
  reset,
  use,
  ads,
  // Guarda-chuva do Montilla CMS. `ads` fica como alias permanente — o custo
  // é uma linha, e há site em produção chamando aquele nome.
  cms: cmsApi,
  cookieConsent,
}

export type Montilla = typeof montillaBase & MontillaPluginNamespaces

/**
 * Pinned to `globalThis` so duplicate copies of `@quero/montilla-core`
 * in the same runtime share one live `montilla` reference — plugin
 * namespaces installed by one copy are visible from the other.
 */
export const montilla: Montilla = globalSingleton<Montilla>(
  'montilla',
  () => montillaBase as Montilla,
)
